/**
 * analyticsTrackers.ts — Higher-level tracking functions for ToToDo events.
 *
 * Ported from v1's `lib/firebase/analytics-trackers.ts`.
 *
 * ⚠️ IMPORTANT: The following events are now emitted automatically by the
 * operations layer via emitEvent() in @totodo/operations. Do NOT call
 * these tracker functions for the same actions — it would cause duplicate
 * Firebase events:
 *   - task_created, task_completed, task_deleted, task_canceled,
 *     task_uncompleted, task_moved, task_scheduled, task_edited,
 *     trash_emptied, project_created, list_created, area_created,
 *     reminder_created, checklist_toggled, checklist_completed,
 *     bulk_completed, bulk_deleted
 *
 * The trackers below are kept for UI-only events that have no
 * corresponding operation (e.g. search, filter, sort, settings, errors).
 *
 * All functions are fire-and-forget — they never throw.
 */

import { logAnalyticsEvent, logScreenView } from "./analytics"

// ---------------------------------------------------------------------------
// Reminder events (UI-only — reminder_created handled by ops layer)
// ---------------------------------------------------------------------------

export function trackReminderTriggered(
  deliveryStatus?: "success" | "failed",
): void {
  void logAnalyticsEvent("reminder_triggered", {
    delivery_status: deliveryStatus ?? "success",
  })
}

export function trackReminderDismissed(): void {
  void logAnalyticsEvent("reminder_dismissed")
}

// ---------------------------------------------------------------------------
// Checklist events — checklist_toggled + checklist_completed handled by ops layer
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// User engagement events
// ---------------------------------------------------------------------------

export function trackSettingsChanged(
  settingName: string,
  settingValue?: string,
): void {
  void logAnalyticsEvent("settings_changed", {
    setting_name: settingName.slice(0, 40),
    setting_value: (settingValue ?? "").slice(0, 40),
  })
}

export function trackAppShared(
  platform?: "whatsapp" | "email" | "sms" | "other",
): void {
  void logAnalyticsEvent("app_shared", {
    share_platform: platform ?? "other",
  })
}

export function trackFilterApplied(filterType?: string): void {
  void logAnalyticsEvent("filter_applied", {
    filter_type: filterType ?? "unknown",
  })
}

export function trackSearchPerformed(queryLength?: number): void {
  void logAnalyticsEvent("search_performed", {
    query_length: queryLength ?? 0,
  })
}

export function trackSortChanged(
  sortType?: "date" | "priority" | "name" | "created",
): void {
  void logAnalyticsEvent("sort_changed", {
    sort_type: sortType ?? "date",
  })
}

// ---------------------------------------------------------------------------
// Sync events
// ---------------------------------------------------------------------------

export function trackSyncCompleted(data?: {
  itemsCreated?: number
  itemsUpdated?: number
  itemsDeleted?: number
  durationSeconds?: number
}): void {
  void logAnalyticsEvent("sync_completed", {
    items_created: data?.itemsCreated ?? 0,
    items_updated: data?.itemsUpdated ?? 0,
    items_deleted: data?.itemsDeleted ?? 0,
    duration_seconds: data?.durationSeconds ?? 0,
  })
}

export function trackSyncFailed(errorType?: string): void {
  void logAnalyticsEvent("sync_failed", {
    error_type: (errorType ?? "unknown").slice(0, 40),
  })
}

// ---------------------------------------------------------------------------
// Subscription events
// ---------------------------------------------------------------------------

export function trackSubscriptionStarted(
  planType?: "free" | "standard" | "premium",
): void {
  void logAnalyticsEvent("subscription_started", {
    plan_type: planType ?? "standard",
  })
}

export function trackSubscriptionCancelled(reason?: string): void {
  void logAnalyticsEvent("subscription_cancelled", {
    reason: (reason ?? "user_request").slice(0, 40),
  })
}

// ---------------------------------------------------------------------------
// Screen navigation events
// ---------------------------------------------------------------------------

export function trackScreenNavigation(screenName: string): void {
  void logScreenView(screenName)
}

// ---------------------------------------------------------------------------
// Performance events
// ---------------------------------------------------------------------------

export function trackAppStartup(startupTimeMs?: number): void {
  void logAnalyticsEvent("app_startup", {
    startup_time_ms: startupTimeMs ?? 0,
  })
}

export function trackDataLoadingTime(
  component: string,
  timeMs?: number,
): void {
  void logAnalyticsEvent("data_loading_time", {
    component: component.slice(0, 40),
    time_ms: timeMs ?? 0,
  })
}

export function trackApiCallDuration(
  endpoint: string,
  durationMs: number,
  statusCode: number,
): void {
  void logAnalyticsEvent("api_call_duration", {
    endpoint: endpoint.slice(0, 40),
    duration_ms: durationMs,
    status_code: statusCode,
  })
}

// ---------------------------------------------------------------------------
// Error events
// ---------------------------------------------------------------------------

export function trackErrorOccurred(
  errorType: string,
  errorMessage?: string,
): void {
  void logAnalyticsEvent("error_occurred", {
    error_type: errorType.slice(0, 40),
    error_message: (errorMessage ?? "").slice(0, 100),
  })
}

// ---------------------------------------------------------------------------
// Batch events — handled by ops layer (bulk_completed, bulk_deleted)
// Use trackFeatureUsed() for batch UI-specific tracking.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Feature usage (generic tracker for any feature)
// ---------------------------------------------------------------------------

export function trackFeatureUsed(
  featureName: string,
  context?: Record<string, string | number | boolean>,
): void {
  const params: Record<string, string | number | boolean> = {
    feature_name: featureName.slice(0, 40),
  }
  if (context) {
    // Max 24 extra params (25 total - 1 for feature_name)
    for (const [key, value] of Object.entries(context).slice(0, 24)) {
      params[key.slice(0, 40)] =
        typeof value === "string" ? value.slice(0, 256) : value
    }
  }
  void logAnalyticsEvent("feature_used", params)
}
